import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { trackFunnelEvent } from "@/services/FunnelAnalytics";
import styleOilPainting from "@/assets/style-oil-painting.jpg";
import styleWatercolor from "@/assets/style-watercolor.jpg";
import styleDisney from "@/assets/style-disney.jpg";

const sourcePhoto = "/site-assets/source-photo.jpg";

const styles = [
  {
    id: 1,
    title: "Peinture à l'huile",
    description: "Pour un portrait profond, texturé, avec un vrai effet tableau de salon.",
    bestFor: "famille, couple, cadeau premium",
    source: "gallery_oil_painting",
    image: styleOilPainting,
    alt: "Photo transformée en peinture à l'huile personnalisée — portrait artistique",
  },
  {
    id: 2,
    title: "Aquarelle",
    description: "Pour un rendu lumineux et doux, idéal avec des enfants ou une photo de famille.",
    bestFor: "famille, naissance, déco claire",
    source: "gallery_watercolor",
    image: styleWatercolor,
    alt: "Photo transformée en aquarelle personnalisée — style lumineux et poétique",
  },
  {
    id: 3,
    title: "Animation 3D",
    description: "Pour une transformation expressive et moderne, souvent très adaptée aux animaux.",
    bestFor: "chien, chat, cadeau ludique",
    source: "gallery_animation_3d",
    image: styleDisney,
    alt: "Photo transformée en personnage animation 3D — style dessin animé moderne",
  },
];

const proofSteps = [
  "Photo personnelle importée",
  "Style artistique choisi",
  "Tableau imprimé prêt à offrir",
];

const GallerySection = () => {
  const navigate = useNavigate();

  const startCreateFlow = (source: string) => {
    trackFunnelEvent("homepage_cta_clicked", { source });
    navigate(`/create?source=${source}`);
  };

  return (
    <section id="gallery" className="section-shell relative py-24">
      <div className="container relative mx-auto px-6">
        <div className="mb-10 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
          <div className="max-w-3xl">
            <p className="eyebrow mb-3">Preuve visuelle</p>
            <h2 className="section-title text-foreground">
              Visualisez le passage <span className="text-premium">photo → œuvre → tableau imprimé</span>.
            </h2>
            <p className="mt-4 max-w-2xl text-lg leading-8 text-foreground/65">
              Avant de commander, l'objectif est simple : partir d'une photo nette, tester un style adapté à votre usage, puis choisir le support imprimé qui mettra le résultat en valeur.
            </p>
          </div>
          <Button
            variant="outline"
            size="lg"
            className="h-12 rounded-full border-primary/20 bg-white/60 px-6 text-primary hover:bg-white/80"
            onClick={() => startCreateFlow("gallery_header_cta")}
          >
            Essayer avec ma photo
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="mb-8 grid gap-4 rounded-[2rem] border border-white/65 bg-white/62 p-4 shadow-card backdrop-blur-md lg:grid-cols-[0.82fr_1fr_0.82fr] lg:p-6">
          <div className="overflow-hidden rounded-[1.55rem] bg-white shadow-card">
            <img
              src={sourcePhoto}
              alt="Photo originale utilisée comme point de départ avant transformation artistique"
              loading="lazy"
              className="h-64 w-full object-cover object-center lg:h-full"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3 lg:py-2">
            {proofSteps.map((step, index) => (
              <div key={step} className="rounded-[1.35rem] border border-primary/10 bg-white/75 p-4 text-center shadow-sm">
                <div className="mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-extrabold text-primary-foreground">
                  {index + 1}
                </div>
                <p className="text-sm font-semibold leading-6 text-foreground/72">{step}</p>
              </div>
            ))}
          </div>
          <div className="overflow-hidden rounded-[1.55rem] bg-[#f7efe8] shadow-card">
            <img
              src={styleWatercolor}
              alt="Exemple de rendu artistique prêt à être imprimé en tableau personnalisé"
              loading="lazy"
              className="h-64 w-full object-cover object-center lg:h-full"
            />
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr_0.9fr]">
          {styles.map((style, index) => (
            <button
              key={style.id}
              type="button"
              className={`group hover-lift relative cursor-pointer overflow-hidden rounded-[2rem] border border-white/60 bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(247,239,233,0.92))] p-5 text-left shadow-card transition-premium ${
                index === 0 ? "lg:row-span-2" : ""
              }`}
              onClick={() => startCreateFlow(style.source)}
            >
              <div className={`relative overflow-hidden rounded-[1.6rem] ${index === 0 ? "mb-7" : "mb-5"}`}>
                <img
                  src={style.image}
                  alt={style.alt}
                  loading={index === 0 ? "eager" : "lazy"}
                  className={`w-full object-cover transition-premium group-hover:scale-[1.04] ${
                    index === 0 ? "h-[30rem]" : "h-72"
                  }`}
                />
                <div className="absolute left-4 top-4 rounded-full bg-white/82 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-primary shadow-card backdrop-blur-sm">
                  Idéal : {style.bestFor}
                </div>
              </div>

              <h3 className="font-display text-4xl leading-none text-foreground">
                {style.title}
              </h3>

              <p className="mt-3 text-sm leading-7 text-foreground/65 md:text-[0.95rem]">
                {style.description}
              </p>
            </button>
          ))}
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-5 rounded-[2rem] border border-white/60 bg-[hsl(15_18%_18%)] px-8 py-7 text-white shadow-luxury lg:flex-row lg:items-center">
          <div>
            <p className="max-w-xl text-lg leading-8 text-white/82">
              16+ styles disponibles pour trouver le rendu qui correspond à votre photo et à l'usage prévu.
            </p>
            <div className="mt-3 flex flex-wrap gap-3 text-sm text-white/68">
              {proofSteps.map((step) => (
                <span key={step} className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  {step}
                </span>
              ))}
            </div>
          </div>
          <Button
            variant="premium"
            size="lg"
            className="h-14 rounded-full px-8"
            onClick={() => startCreateFlow("gallery_all_styles_cta")}
          >
            Explorer les 16+ styles
          </Button>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
